import {
  Box,
  Card,
  Flex,
  Image,
  JaneGoldIcon,
  Tag,
  TagFilledIcon,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';

import { CartProductActions } from '../cartProductActions/cartProductActions';

import styles from './cartProduct.module.css';

export interface CartProductProps {
  bloomUserExperience?: boolean;
  brand?: string | null;
  brandDiscountText?: string | null;
  brandSubtype?: string | null;
  category?: string | null;
  count: number;
  discountPercentage: number;
  fullPriceIsDiscounted: boolean;
  hideStrikethrough: boolean;
  isDiscounted: boolean;
  maxCartQuantity: number;
  name: string;
  needsSignInForEligibility: boolean;
  onEditQuantity: (qty: number) => void;
  onGoToProductDetailPage?: () => void;
  onRemoveProduct: () => void;
  photoUrl: string;
  productFullPrice: number;
  productPrice: number | null;
  shouldShowGold: boolean;
  specialBadgeColor?: string;
  specialTitle?: string | null;
  weightString: string;
}

function SpecialsTag({
  specialBadgeColor,
  title,
}: {
  specialBadgeColor?: string;
  title: string;
}) {
  return (
    <Flex
      alignItems="center"
      style={
        specialBadgeColor
          ? { '--colors-info-main': specialBadgeColor }
          : undefined
      }
    >
      <TagFilledIcon size="sm" color="info" />
      <Typography color="info" ml={4}>
        {title}
      </Typography>
    </Flex>
  );
}

export const CartProduct = ({
  bloomUserExperience,
  brand,
  brandDiscountText,
  brandSubtype,
  category,
  count,
  discountPercentage,
  fullPriceIsDiscounted,
  hideStrikethrough,
  isDiscounted,
  maxCartQuantity,
  name,
  needsSignInForEligibility,
  onEditQuantity,
  onGoToProductDetailPage,
  onRemoveProduct,
  photoUrl,
  productFullPrice,
  productPrice,
  shouldShowGold,
  specialBadgeColor,
  specialTitle,
  weightString,
}: CartProductProps) => {
  const isMobile = useMobileMediaQuery({});

  const price = bloomUserExperience ? null : (
    <Typography variant="body-semibold" textAlign="right">
      {formatCurrency(productPrice! * count)}
    </Typography>
  );

  const productDetails = (
    <Flex
      className={styles['productDetailsContainer']}
      mt={isMobile ? 16 : undefined}
      width="100%"
      overflow="hidden"
      flexDirection="column"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Typography
          truncateAt={isMobile ? '100%' : '85%'}
          variant="body-semibold"
        >
          {name}
        </Typography>
        {!isMobile && price}
      </Flex>
      <Typography className={styles['capitalizeText']} color="text-light">
        {brand}
      </Typography>
      <Typography className={styles['capitalizeText']} color="text-light">
        {brandSubtype}
        {brandSubtype && category ? ' - ' : ''}
        {category}
      </Typography>
      <Typography className={styles['capitalizeText']} color="text-light">
        {formatCurrency(productPrice)}
        {weightString}
      </Typography>

      {specialTitle && (!bloomUserExperience || isDiscounted) && (
        <SpecialsTag
          title={specialTitle}
          specialBadgeColor={specialBadgeColor}
        />
      )}
      {shouldShowGold && (
        <Flex alignItems="flex-start" gap={4} width="100%">
          <Box mt={4}>
            <JaneGoldIcon altText="jane-gold-icon" size="sm" />
          </Box>
          <Typography>
            {brandDiscountText}{' '}
            {needsSignInForEligibility && (
              <Typography as="span" color="grays-mid">
                (Sign in for eligibility)
              </Typography>
            )}
          </Typography>
        </Flex>
      )}
    </Flex>
  );

  return (
    <Card
      className={styles['cartProduct']}
      data-testid="cart-item"
      flat
      width="100%"
    >
      <Card.Content>
        <Flex flexDirection="column" height="100%">
          <Flex
            flexDirection="row"
            height="100%"
            width="100%"
            minWidth={isMobile ? 'calc(95vw - 80px)' : ''}
            justifyContent={isMobile ? 'space-between' : undefined}
            gap={16}
          >
            <Flex
              flexDirection="column"
              width={isMobile ? '100%' : undefined}
              maxWidth={bloomUserExperience ? '75%' : undefined}
              onClick={onGoToProductDetailPage}
              ariaLabel={`view menu product details for ${name}`}
            >
              <Flex
                alignItems="flex-start"
                justifyContent="space-between"
                width="100%"
              >
                <Flex
                  className={styles['productDetailsContainer']}
                  alignSelf={isMobile ? 'flex-start' : ''}
                >
                  {bloomUserExperience ? (
                    <Flex
                      width={isMobile ? '88px' : '96px'}
                      height={isMobile ? '88px' : '96px'}
                    >
                      <Flex position="absolute">
                        <Image
                          src={photoUrl}
                          altText={name}
                          border
                          borderRadius={isMobile ? '16px' : 'rounded'}
                          height={isMobile ? '88px' : '96px'}
                          width={isMobile ? '88px' : '96px'}
                          responsive
                          sizes="thumbnail"
                        />
                      </Flex>
                      {(discountPercentage ?? 0) > 0 && (
                        <Flex
                          zIndex={1}
                          position="relative"
                          width="100%"
                          justifyContent="flex-end"
                          p={8}
                        >
                          <Tag
                            label={
                              hideStrikethrough
                                ? 'Deal'
                                : `-${discountPercentage}%`
                            }
                            color="text-info"
                            background="info"
                          />
                        </Flex>
                      )}
                    </Flex>
                  ) : (
                    <Image
                      src={photoUrl}
                      altText={name}
                      border
                      borderRadius={isMobile ? '16px' : 'rounded'}
                      height="72px"
                      width="72px"
                      responsive
                      sizes="thumbnail"
                    />
                  )}
                </Flex>
                {isMobile && price}
              </Flex>

              {isMobile && productDetails}
            </Flex>

            {!isMobile && (
              <Flex width="100%" overflow="hidden">
                {productDetails}
              </Flex>
            )}

            <Flex
              flexDirection="column"
              justifyContent="space-between"
              shrink={bloomUserExperience}
            >
              {bloomUserExperience ? (
                <Flex
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body-semibold"
                    textAlign="right"
                    ml={8}
                    color={specialTitle ? 'info' : 'inherit'}
                  >
                    {formatCurrency(productPrice! * count)}
                  </Typography>
                  {isDiscounted &&
                    fullPriceIsDiscounted &&
                    !hideStrikethrough && (
                      <Typography
                        variant={
                          bloomUserExperience ? 'body-regular' : 'body-semibold'
                        }
                        textAlign="right"
                        strikeThrough
                        color="text-light"
                        ml={8}
                      >
                        {formatCurrency(productFullPrice * count)}
                      </Typography>
                    )}
                </Flex>
              ) : null}
            </Flex>
          </Flex>
          <Flex width="100%" mt={24}>
            <CartProductActions
              count={count}
              name={name}
              handleEditQuantity={onEditQuantity}
              handleRemoveProduct={onRemoveProduct}
              maxQuantity={maxCartQuantity}
            />
          </Flex>
        </Flex>
      </Card.Content>
    </Card>
  );
};
